import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; password: string; firstName?: string; lastName?: string }) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  /**
   * Persist a fresh JWT without touching the rest of the auth state.
   * Used by the session-expiry hook after a successful `/auth/refresh`.
   * Also keeps the axios default `Authorization` header in sync so
   * subsequent requests use the new token immediately.
   */
  setToken: (token: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/login', { email, password });
          const { user, token } = response.data;
          set({ user, token, isAuthenticated: true, isLoading: false });
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Login failed',
            isLoading: false
          });
          throw error;
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/register', data);
          const { user, token } = response.data;
          set({ user, token, isAuthenticated: true, isLoading: false });
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Registration failed',
            isLoading: false
          });
          throw error;
        }
      },

      logout: () => {
        // The backend /auth/logout route was removed in the ZewstID cutover, so
        // POSTing it only produced a 401 on the stale bearer (and spammed it on
        // every token-loss transition). The real end-session is
        // oidc.signoutRedirect() (PortalLayout). Here we just clear the local
        // mirror; OIDC remains the source of truth.
        delete api.defaults.headers.common['Authorization'];
        set({ user: null, token: null, isAuthenticated: false });
      },

      setToken: (token: string) => {
        if (!token) return;
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        set({ token });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, token: state.token, isAuthenticated: state.isAuthenticated }),
    }
  )
);

// Set token on app load if exists
const token = useAuthStore.getState().token;
if (token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Listen for `auth:unauthorized` events emitted by the api interceptor on a
// 401. Flipping `isAuthenticated` to false lets React Router redirect to
// /login via its existing guard, instead of doing a hard `window.location`
// nav that discards in-progress IDE state.
if (typeof window !== 'undefined') {
  window.addEventListener('auth:unauthorized', () => {
    delete api.defaults.headers.common['Authorization'];
    useAuthStore.setState({ user: null, token: null, isAuthenticated: false });
  });
}
